import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox
} from "@mui/material";
import { AppColors } from "../../../../util/AppColors";

const purpleBtnStyle = {
  backgroundColor: "#7b1fa2",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  height: "30px",
  padding: "0 14px",
  fontSize: "13px",
  fontWeight: "600",
  fontFamily: "Montserrat",
  cursor: "pointer",
  textTransform: "capitalize",
  boxShadow: "none"
};

const tableHeaderCellSty = {
  fontWeight: "bold",
  fontFamily: "Montserrat",
  color: AppColors.gray3,
  padding: "8px 16px",
  borderBottom: "1px solid #e0e0e0"
};

const tableBodyCellSty = {
  fontFamily: "Montserrat",
  padding: "10px 16px",
  borderBottom: "1px solid #e0e0e0",
  color: "#1c2434"
};

const PharmacyDetailsTables = ({ record }) => {
  const [selectedConsumables, setSelectedConsumables] = useState([]);
  const [selectedAdminister, setSelectedAdminister] = useState([]);

  if (!record) return null;

  const consumables = record.consumables ?? [];
  const administration = record.administration ?? [];

  // Consumables checkbox logic
  const handleToggleConsumable = (id) => {
    setSelectedConsumables((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleAllConsumables = () => {
    if (selectedConsumables.length === consumables.length) {
      setSelectedConsumables([]);
    } else {
      setSelectedConsumables(consumables.map((c) => c.id));
    }
  };

  // Administer checkbox logic
  const handleToggleAdminister = (id) => {
    setSelectedAdminister((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleAllAdminister = () => {
    if (selectedAdminister.length === administration.length) {
      setSelectedAdminister([]);
    } else {
      setSelectedAdminister(administration.map((a) => a.id));
    }
  };

  // Actions click handlers
  const handleTransferItem = (itemType, item) => {
    alert(`Transfer ${itemType}: ${item.name} (Batch: ${item.batchNumber})`);
  };

  const handleTransferAll = (itemType, items, selectedIds) => {
    const listToTransfer = selectedIds.length > 0 
      ? items.filter(item => selectedIds.includes(item.id)) 
      : items;
    if (listToTransfer.length === 0) {
      alert(`No items selected to transfer for ${itemType}.`);
      return;
    }
    const names = listToTransfer.map(item => item.name).join(", ");
    alert(`Transfer All ${itemType}: ${names}`);
  };

  const handleAddItem = (itemType) => {
    alert(`Add new item clicked for ${itemType}`);
  };

  return (
    <Box display="flex" flexDirection="column" gap={4} sx={{ width: "100%" }}>
      
      {/* Consumables Section */}
      <Paper
        elevation={0}
        sx={{
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "16px",
              color: "#1c2434",
              fontFamily: "Montserrat"
            }}
          >
            Consumables
          </Typography>
          <button
            onClick={() => handleTransferAll("Consumables", consumables, selectedConsumables)}
            style={purpleBtnStyle}
          >
            Transfer all
          </button>
        </Box>

        <TableContainer component={Box} sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", mb: 2 }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
              <TableRow>
                <TableCell sx={{ ...tableHeaderCellSty, width: "50px" }}>
                  <Checkbox
                    size="small"
                    indeterminate={
                      selectedConsumables.length > 0 &&
                      selectedConsumables.length < consumables.length
                    }
                    checked={consumables.length > 0 && selectedConsumables.length === consumables.length}
                    onChange={handleToggleAllConsumables}
                    sx={{ padding: 0 }}
                  />
                </TableCell>
                <TableCell sx={tableHeaderCellSty}>Name</TableCell>
                <TableCell sx={tableHeaderCellSty}>Batch Number</TableCell>
                <TableCell sx={tableHeaderCellSty}>Quantity</TableCell>
                <TableCell sx={tableHeaderCellSty}>Status</TableCell>
                <TableCell sx={tableHeaderCellSty}>Reason</TableCell>
                <TableCell sx={tableHeaderCellSty}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consumables.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ ...tableBodyCellSty, color: AppColors.gray3, py: 3 }}>
                    No consumables prescribed.
                  </TableCell>
                </TableRow>
              ) : (
                consumables.map((item) => {
                  const isChecked = selectedConsumables.includes(item.id);
                  return (
                    <TableRow key={item.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell sx={tableBodyCellSty}>
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          onChange={() => handleToggleConsumable(item.id)}
                          sx={{ padding: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableBodyCellSty, fontWeight: 500 }}>{item.name}</TableCell>
                      <TableCell sx={tableBodyCellSty}>{item.batchNumber}</TableCell>
                      <TableCell sx={{ ...tableBodyCellSty, fontWeight: 600 }}>{item.quantity}</TableCell>
                      <TableCell sx={tableBodyCellSty}>
                        <span
                          style={{
                            backgroundColor: "#fff8e1",
                            color: "#b78103",
                            fontSize: "11px",
                            fontWeight: "bold",
                            padding: "3px 8px",
                            borderRadius: "4px",
                            fontFamily: "Montserrat"
                          }}
                        >
                          {item.status}
                        </span>
                      </TableCell>
                      <TableCell sx={tableBodyCellSty}>{item.reason}</TableCell>
                      <TableCell sx={tableBodyCellSty}>
                        <button
                          onClick={() => handleTransferItem("Consumables", item)}
                          style={purpleBtnStyle}
                        >
                          Transfer
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex">
          <button
            onClick={() => handleAddItem("Consumables")}
            style={{
              backgroundColor: "#f1f3f4",
              color: "#1c2434",
              border: "none",
              borderRadius: "5px",
              height: "30px",
              padding: "0 16px",
              fontSize: "13px",
              fontWeight: "700",
              fontFamily: "Montserrat",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center"
            }}
          >
            + Add
          </button>
        </Box>
      </Paper>

      {/* Administer Section */}
      <Paper
        elevation={0}
        sx={{
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "16px",
              color: "#1c2434",
              fontFamily: "Montserrat"
            }}
          >
            Administer
          </Typography>
          <button
            onClick={() => handleTransferAll("Administer", administration, selectedAdminister)}
            style={purpleBtnStyle}
          >
            Transfer all
          </button>
        </Box>

        <TableContainer component={Box} sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", mb: 2 }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
              <TableRow>
                <TableCell sx={{ ...tableHeaderCellSty, width: "50px" }}>
                  <Checkbox
                    size="small"
                    indeterminate={
                      selectedAdminister.length > 0 &&
                      selectedAdminister.length < administration.length
                    }
                    checked={administration.length > 0 && selectedAdminister.length === administration.length}
                    onChange={handleToggleAllAdminister}
                    sx={{ padding: 0 }}
                  />
                </TableCell>
                <TableCell sx={tableHeaderCellSty}>Name</TableCell>
                <TableCell sx={tableHeaderCellSty}>Batch Number</TableCell>
                <TableCell sx={tableHeaderCellSty}>Dosage</TableCell>
                <TableCell sx={tableHeaderCellSty}>Route</TableCell>
                <TableCell sx={tableHeaderCellSty}>Status</TableCell>
                <TableCell sx={tableHeaderCellSty}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {administration.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ ...tableBodyCellSty, color: AppColors.gray3, py: 3 }}>
                    No administration items prescribed.
                  </TableCell>
                </TableRow>
              ) : (
                administration.map((item) => {
                  const isChecked = selectedAdminister.includes(item.id);
                  return (
                    <TableRow key={item.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell sx={tableBodyCellSty}>
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          onChange={() => handleToggleAdminister(item.id)}
                          sx={{ padding: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={tableBodyCellSty}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: "Montserrat", color: "#1c2434" }}>
                            {item.name}
                          </Typography>
                          {item.isScheduled && (
                            <span
                              style={{
                                backgroundColor: "#fff8e1",
                                color: "#b78103",
                                fontSize: "10px",
                                fontWeight: "bold",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                textTransform: "uppercase",
                                fontFamily: "Montserrat"
                              }}
                            >
                              SCHEDULED
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={tableBodyCellSty}>{item.batchNumber}</TableCell>
                      <TableCell sx={{ ...tableBodyCellSty, fontWeight: 600 }}>{item.dosage}</TableCell>
                      <TableCell sx={tableBodyCellSty}>{item.route}</TableCell>
                      <TableCell sx={tableBodyCellSty}>
                        <span
                          style={{
                            backgroundColor: "#fff8e1",
                            color: "#b78103",
                            fontSize: "11px",
                            fontWeight: "bold",
                            padding: "3px 8px",
                            borderRadius: "4px",
                            fontFamily: "Montserrat"
                          }}
                        >
                          {item.status}
                        </span>
                      </TableCell>
                      <TableCell sx={tableBodyCellSty}>
                        <button
                          onClick={() => handleTransferItem("Administer", item)}
                          style={purpleBtnStyle}
                        >
                          Transfer
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex">
          <button
            onClick={() => handleAddItem("Administer")}
            style={{
              backgroundColor: "#f1f3f4",
              color: "#1c2434",
              border: "none",
              borderRadius: "5px",
              height: "30px",
              padding: "0 16px",
              fontSize: "13px",
              fontWeight: "700",
              fontFamily: "Montserrat",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center"
            }}
          >
            + Add
          </button>
        </Box>
      </Paper>

    </Box>
  );
};

export default PharmacyDetailsTables;
